import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { X, Shield, Copy, Check } from 'lucide-react'
import { useTheme, getThemeClass, FileIcon, fileIconProps, miniappIconProps } from '@papan/core-ui/ui'
import { isChainPointerRow } from '@papan/types'
import { useTranslation } from '@papan/i18n'
import { filesApi } from '@papan/files/client'
import type { FileItem } from './useFiles'

/**
 * File/folder "Details" dialog — identity (icon, name, kind), the facts
 * (size/version or folder counts, your access, modified), and WHERE it is:
 * the drive path and the item's id, each copyable. Extracted from DriveView so
 * the Projects/MiniApps card and Shared view context menus open the SAME dialog
 * (one source).
 *
 * Callers may hand in a minimal `{ id, name, isFolder }` (the drive background
 * menu builds one for the folder you are standing in), so every other field is
 * optional here.
 */
function formatFileSize(bytes: number): string {
  if (bytes === 0) return '--'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`
}

const isDriveRootId = (id: string) => id.startsWith('papan_')

/** The drive-relative path: breadcrumbs carry the item itself and exclude the drive root. */
export function pathFromBreadcrumbs(crumbs: Array<{ name: string }>): string {
  return '/' + crumbs.map((c) => c.name).join('/')
}

/** A copy button that confirms in place (icon → check) instead of raising a toast. */
function CopyButton({ value, label, copiedLabel, testId }: { value: string; label: string; copiedLabel: string; testId: string }) {
  const [copied, setCopied] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current) }, [])
  const onCopy = () => {
    // `navigator.clipboard` is undefined outside a secure context (dev over a LAN IP).
    if (!navigator.clipboard) return
    void navigator.clipboard.writeText(value).then(() => {
      setCopied(true)
      if (timer.current) clearTimeout(timer.current)
      timer.current = setTimeout(() => setCopied(false), 1500)
    }).catch(() => {})
  }
  return (
    <button
      type="button"
      onClick={onCopy}
      data-testid={testId}
      aria-label={copied ? copiedLabel : label}
      title={copied ? copiedLabel : label}
      className={`shrink-0 p-1.5 rounded-md transition-colors ${copied ? 'text-emerald-500' : 'themed-text-muted hover:themed-text hover:themed-bg-tertiary'}`}
    >
      {copied ? <Check size={14} /> : <Copy size={14} />}
    </button>
  )
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="text-[11px] uppercase tracking-wide themed-text-muted">{label}</div>
      <div className="mt-0.5 text-sm themed-text truncate">{children}</div>
    </div>
  )
}

export function FileInfoDialog({ file, onClose }: { file: FileItem; onClose: () => void }) {
  const { t } = useTranslation('files')
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const [stats, setStats] = useState<{ fileCount: number; folderCount: number; totalSize: number; truncated?: boolean; permission: string } | null>(null)
  const [versionInfo, setVersionInfo] = useState<{ count: number; latest: number } | null>(null)
  // undefined = still loading; null = unavailable (the id has no readable path).
  const [crumbs, setCrumbs] = useState<Array<{ id: string; name: string }> | null | undefined>(undefined)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    setCrumbs(undefined)
    const promises: Promise<void>[] = [
      filesApi.getFileStats(file.id).then(setStats).catch(() => {}),
    ]
    if (!file.isFolder) {
      promises.push(
        filesApi.getVersions(file.id).then(res => {
          const v = res.versions
          const latest = v.reduce((max, ver) => Math.max(max, ver.versionNumber ?? 0), 0)
          setVersionInfo({ count: v.length, latest })
        }).catch(() => {})
      )
    }
    Promise.all(promises).finally(() => setLoading(false))
    let cancelled = false
    filesApi.getBreadcrumbs(file.id)
      // The drive root (`papan_<tenantId>`) has no crumbs of its own — it IS
      // "/". Any other id with no crumbs has no readable path.
      .then((c) => { if (!cancelled) setCrumbs(c.length > 0 || isDriveRootId(file.id) ? c : null) })
      .catch(() => { if (!cancelled) setCrumbs(null) })
    return () => { cancelled = true }
  }, [file.id, file.isFolder])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  const PERM_LABEL: Record<string, string> = {
    owner: t('fileInfo.access.owner'), admin: t('fileInfo.access.admin'), readwrite: t('fileInfo.access.readwrite'),
    readcopy: t('fileInfo.access.readcopy'), hidden: t('fileInfo.access.hidden'),
    // Defense-in-depth: stale rows from before migration 0061 may still carry
    // 'read' until a write touches them. Fold to the post-migration label.
    read: t('fileInfo.access.readcopy'),
  }

  const atLeast = (value: string) => (stats?.truncated ? t('fileInfo.atLeast', { value }) : value)

  const formatDate = (dateStr: string) => {
    try {
      const d = new Date(dateStr)
      return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    } catch { return dateStr }
  }

  const ext = file.name.includes('.') ? file.name.split('.').pop()?.toUpperCase() : undefined
  const kind = file.isFolder
    ? t('fileInfo.kind.folder')
    : ext ? t('fileInfo.kind.file', { ext }) : t('fileInfo.kind.fileGeneric')
  const path = crumbs ? pathFromBreadcrumbs(crumbs) : null

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="file-info-title"
        data-testid="file-info-dialog"
        className={`relative z-10 rounded-xl shadow-2xl w-[400px] max-w-full overflow-hidden border themed-border ${themeClass} themed-bg-secondary`}
      >
        {/* Identity */}
        <div className="flex items-start gap-3 px-5 pt-5 pb-4">
          <div className="shrink-0 w-11 h-11 rounded-lg flex items-center justify-center themed-bg-tertiary">
            <FileIcon {...fileIconProps(file)} size={24} {...miniappIconProps(file)} />
          </div>
          <div className="min-w-0 flex-1">
            <h3 id="file-info-title" className="text-sm font-semibold themed-text break-all leading-snug">{file.name}</h3>
            <div className="mt-0.5 text-xs themed-text-muted">
              {kind}
              {!file.isFolder && typeof file.size === 'number' && <> · {formatFileSize(file.size)}</>}
            </div>
          </div>
          <button onClick={onClose} aria-label={t('fileInfo.close')} className="shrink-0 -mr-1 -mt-1 p-1 themed-text-muted hover:themed-text transition-colors rounded">
            <X size={16} />
          </button>
        </div>

        {/* Facts */}
        <div className="px-5 py-4 border-t themed-border grid grid-cols-2 gap-x-6 gap-y-3">
          {!file.isFolder && versionInfo !== null && (
            <Fact label={t('fileInfo.version')}>v{versionInfo.latest} <span className="themed-text-muted">({t('fileInfo.versionCount', { count: versionInfo.count })})</span></Fact>
          )}
          {file.isFolder && stats && (
            <>
              {/* A truncated answer is a lower bound — never shown as exact. */}
              <Fact label={t('fileInfo.folders')}>{atLeast(String(stats.folderCount))}</Fact>
              <Fact label={t('fileInfo.files')}>{atLeast(String(stats.fileCount))}</Fact>
              {/* `formatFileSize(0)` is '--' (no size) — as a minimum it must read "0 B". */}
              <Fact label={t('fileInfo.totalSize')}>{atLeast(stats.truncated && stats.totalSize === 0 ? '0 B' : formatFileSize(stats.totalSize))}</Fact>
              {stats.truncated && (
                <div data-testid="file-info-truncated" className="col-span-2 text-xs themed-text-muted">{t('fileInfo.truncatedNote')}</div>
              )}
            </>
          )}
          {stats && (
            <Fact label={t('fileInfo.yourAccess')}>
              <span className="inline-flex items-center gap-1">
                <Shield size={12} className="shrink-0" />
                {stats.permission === 'hidden' ? PERM_LABEL['read'] : (PERM_LABEL[stats.permission] || stats.permission)}
              </span>
            </Fact>
          )}
          {file.updatedAt && <Fact label={t('fileInfo.modified')}>{formatDate(file.updatedAt)}</Fact>}
          {/* D10: a `.chain` whose author can't open or share its target (or that an app /
              share visitor wrote) is not followed by share links or apps until a
              person who can open and share the target approves it. The listing decides. */}
          {isChainPointerRow(file) && file.chainNeedsApproval && !file.chainBroken && (
            <div data-testid="file-info-chain-needs-approval" className="col-span-2 min-w-0">
              <div className="text-[11px] uppercase tracking-wide themed-text-muted">{t('fileInfo.linksAndApps')}</div>
              <div className="mt-0.5 text-sm text-amber-500">{t('driveView.chain.needsApproval')}</div>
              <div className="mt-0.5 text-xs themed-text-muted">{t('driveView.chain.needsApprovalHint')}</div>
            </div>
          )}
          {loading && !stats && <div className="col-span-2 text-xs themed-text-muted opacity-60">{t('fileInfo.loading')}</div>}
        </div>

        {/* Where it is */}
        <div className="px-5 pt-3 pb-5 border-t themed-border space-y-3">
          <div>
            <div className="text-[11px] uppercase tracking-wide themed-text-muted mb-1">{t('fileInfo.location')}</div>
            <div className="flex items-center gap-1 rounded-md themed-bg-primary border themed-border pl-2.5 pr-1 py-1">
              <div data-testid="file-info-path" className="flex-1 min-w-0 text-xs font-mono themed-text break-all py-0.5">
                {crumbs === undefined
                  ? <span className="themed-text-muted">{t('fileInfo.loading')}</span>
                  : crumbs === null
                    ? <span className="themed-text-muted">{t('fileInfo.pathUnavailable')}</span>
                    : crumbs.length === 0
                      ? <span className="font-semibold">/</span>
                      : (
                      <>
                        {crumbs.map((c, i) => (
                          <span key={c.id}>
                            <span className="themed-text-muted">/</span>
                            <span className={i === crumbs.length - 1 ? 'font-semibold' : undefined}>{c.name}</span>
                          </span>
                        ))}
                      </>
                    )}
              </div>
              {path && <CopyButton value={path} testId="file-info-copy-path" label={t('fileInfo.copyPath')} copiedLabel={t('fileInfo.copied')} />}
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wide themed-text-muted mb-1">{t('fileInfo.id')}</div>
            <div className="flex items-center gap-1 rounded-md themed-bg-primary border themed-border pl-2.5 pr-1 py-1">
              <code data-testid="file-info-id" className="flex-1 min-w-0 text-xs font-mono themed-text break-all py-0.5 select-all">{file.id}</code>
              <CopyButton value={file.id} testId="file-info-copy-id" label={t('fileInfo.copyId')} copiedLabel={t('fileInfo.copied')} />
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}
